import type { ReportPnlBrushWindow } from "./reportTradingPnlSeries";

const STORAGE_PREFIX = "tessera.reportDetailUi.v1:";

export type PersistedComparisonPanel = {
  overlayBacktestId: string;
  overlayEnabled: boolean;
};

export type PersistedTradeReviewSlot = {
  chartActiveSymbol?: string;
  selectedTradeId?: string | null;
  candleType?: string;
  date?: string;
  showPnlBackgroundOverlay?: boolean;
  /** Opt-in regime bar bgcolor; default off when unset. */
  showRegimeOverlay?: boolean;
  tradesListOpen?: boolean;
};

export type ReportDetailUiPersistedState = {
  comparisonPanels: PersistedComparisonPanel[];
  tradingPnlSelectedSimIds: string[];
  /** Backtest id → Trading PnL line color (sticky across add/remove/refresh in-session). */
  tradingPnlSimColors: Record<string, string>;
  tradingPnlBrushWindow: ReportPnlBrushWindow | null;
  tradeReviewSlots: Record<string, PersistedTradeReviewSlot>;
};

const EMPTY_STATE: ReportDetailUiPersistedState = {
  comparisonPanels: [],
  tradingPnlSelectedSimIds: [],
  tradingPnlSimColors: {},
  tradingPnlBrushWindow: null,
  tradeReviewSlots: {},
};

function normalizeReportId(reportId: string | undefined | null): string {
  if (reportId == null) return "";
  return String(reportId).trim();
}

function storageKey(reportId: string): string {
  return `${STORAGE_PREFIX}${reportId}`;
}

function canUseSessionStorage(): boolean {
  return typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";
}

function normalizeSimColors(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return {};
  const out: Record<string, string> = {};
  for (const [id, color] of Object.entries(raw as Record<string, unknown>)) {
    const key = String(id ?? "").trim();
    const value = typeof color === "string" ? color.trim() : "";
    if (key && value) out[key] = value;
  }
  return out;
}

export function readReportDetailUiState(
  reportId: string | undefined | null
): ReportDetailUiPersistedState {
  const id = normalizeReportId(reportId);
  if (!id || !canUseSessionStorage()) return { ...EMPTY_STATE, tradingPnlSimColors: {} };
  try {
    const raw = window.sessionStorage.getItem(storageKey(id));
    if (!raw) return { ...EMPTY_STATE, tradingPnlSimColors: {} };
    const parsed = JSON.parse(raw) as Partial<ReportDetailUiPersistedState>;
    return {
      comparisonPanels: Array.isArray(parsed.comparisonPanels) ? parsed.comparisonPanels : [],
      tradingPnlSelectedSimIds: Array.isArray(parsed.tradingPnlSelectedSimIds)
        ? parsed.tradingPnlSelectedSimIds.map(String)
        : [],
      tradingPnlSimColors: normalizeSimColors(parsed.tradingPnlSimColors),
      tradingPnlBrushWindow:
        parsed.tradingPnlBrushWindow &&
        Number.isFinite(Number(parsed.tradingPnlBrushWindow.startMs)) &&
        Number.isFinite(Number(parsed.tradingPnlBrushWindow.endMs))
          ? {
              startMs: Number(parsed.tradingPnlBrushWindow.startMs),
              endMs: Number(parsed.tradingPnlBrushWindow.endMs),
            }
          : null,
      tradeReviewSlots:
        parsed.tradeReviewSlots && typeof parsed.tradeReviewSlots === "object"
          ? parsed.tradeReviewSlots
          : {},
    };
  } catch {
    return { ...EMPTY_STATE, tradingPnlSimColors: {} };
  }
}

export function writeReportDetailUiState(
  reportId: string | undefined | null,
  patch: Partial<ReportDetailUiPersistedState>
): void {
  const id = normalizeReportId(reportId);
  if (!id || !canUseSessionStorage()) return;
  try {
    const current = readReportDetailUiState(id);
    const next: ReportDetailUiPersistedState = {
      comparisonPanels: patch.comparisonPanels ?? current.comparisonPanels,
      tradingPnlSelectedSimIds: patch.tradingPnlSelectedSimIds ?? current.tradingPnlSelectedSimIds,
      tradingPnlSimColors: patch.tradingPnlSimColors ?? current.tradingPnlSimColors,
      tradingPnlBrushWindow:
        patch.tradingPnlBrushWindow !== undefined
          ? patch.tradingPnlBrushWindow
          : current.tradingPnlBrushWindow,
      tradeReviewSlots: patch.tradeReviewSlots ?? current.tradeReviewSlots,
    };
    window.sessionStorage.setItem(storageKey(id), JSON.stringify(next));
  } catch {
    // Quota or private mode — ignore.
  }
}

export function readTradeReviewSlotState(
  reportId: string | undefined | null,
  slotKey: string | undefined | null
): PersistedTradeReviewSlot | null {
  const id = normalizeReportId(reportId);
  const key = normalizeReportId(slotKey);
  if (!id || !key) return null;
  const slot = readReportDetailUiState(id).tradeReviewSlots[key];
  return slot ?? null;
}

export function writeTradeReviewSlotState(
  reportId: string | undefined | null,
  slotKey: string | undefined | null,
  slot: PersistedTradeReviewSlot
): void {
  const id = normalizeReportId(reportId);
  const key = normalizeReportId(slotKey);
  if (!id || !key) return;
  const current = readReportDetailUiState(id);
  writeReportDetailUiState(id, {
    tradeReviewSlots: {
      ...current.tradeReviewSlots,
      [key]: slot,
    },
  });
}

/** Session-only — cleared when the browser tab/window session ends. */
export function reportDetailUiPersistenceLifetimeLabel(): string {
  return "for this browser session";
}
